import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import { requestPasswordValidator } from '@/services/docs-site-auth/requestPasswordValidator';
import type { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import type { OnlineType } from '@/shared/constant/OnlineType';
import { Button, Form, Input, Spin } from 'aui';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import styles from './DocsSiteAuth.module.css';

const PasswordValidator = (props: {
  id: number | string;
  name?: string;
  description?: string;
  onlineType: OnlineType;
  projectId: number;
  imgRender: () => JSX.Element;
  subdirectory?: string;
  visitType?: DocsSiteVisitType;
}) => {
  const { id, imgRender, name, projectId, onlineType, subdirectory, visitType, description } =
    props;
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const envConfig = useEnvConfigModelStore((x) => x);
  const [passwordProtectedForm] = Form.useForm();

  const verifyPermissions = async (password: string) => {
    const [error] = await requestPasswordValidator(
      { id, password, projectId },
      onlineType,
      envConfig,
      clientI18n,
      visitType,
      subdirectory,
    );
    if (!error) {
      window.location.reload();
    }
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = ({ password }: { password: string }) => {
    setSubmitting(true);
    verifyPermissions(password);
    setSubmitting(false);
  };

  useEffect(() => {
    const query = window.location.search;
    const queryParams = new URLSearchParams(query);
    const pwd = queryParams.get('pwd');
    if (pwd) {
      passwordProtectedForm.setFieldsValue({ password: pwd });
      handleSubmit({ password: pwd });
    }
  }, []);

  return (
    <div className="flex max-w-[560px] flex-col items-center justify-center">
      {imgRender()}
      <div className="mb-3 text-center text-h2 font-600 text-color">{name}</div>
      {!!description && (
        <div className="pb-3 text-center text-lg font-400 text-color-sub">{description}</div>
      )}
      <div
        className={clsx(styles.description, 'mb-6 mt-3 text-center text-lg font-400 text-color')}
      >
        {clientI18n('onlineShare.needPassword')}
      </div>
      <Spin spinning={submitting}>
        <div className="flex">
          <Form onFinish={handleSubmit} form={passwordProtectedForm} layout="inline">
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: clientI18n('user.login.password.required'),
                },
              ]}
              className="mr-2"
            >
              <Input.Password
                placeholder={clientI18n('user.login.password.required.placeholder')}
                size="large"
                className="w-[285px]"
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" size="large">
              {clientI18n('view')}
            </Button>
          </Form>
        </div>
      </Spin>
    </div>
  );
};

export default PasswordValidator;
